/// <amd-module name="Wit/LinkedWorkItemsDropdown/Components/LinkedWorkItemsPullRequestDetails" />
import * as React from "react";
import { IReadyableReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer, ReadyableArrayObserver } from "VSSUI/Observer";
import { ITableRow } from "VSSUI/Table";
import { KeyCode } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { getLinkedWorkItemsPullRequestDetailViewService } from "../LinkedWorkItemsPullRequestDetailViewService";
import { IWorkItemEntry } from "../Util/Data";
import { ControlledLinkedWorkItemsDropdown } from "./ControlledLinkedWorkItemsDropdown";
import { LinkedWorkItemsTable } from "./LinkedWorkItemsTable";

interface GitRepository {
    id: string;
    project: { id: string };
}

interface ILinkedWorkItemsPullRequestDetailsProps {
    pullRequestId: number;
    repository: GitRepository;
}

export class LinkedWorkItemsPullRequestDetails extends VssComponent<ILinkedWorkItemsPullRequestDetailsProps> {
    private readonly errorMessage = new ObservableValue<string>("");
    private readonly headerCommandBarItems: IHeaderCommandBarItem[] = [];
    private readonly readonly: boolean;
    private readonly headerRef = React.createRef<Header>();
    private readonly selection = new DropdownSelection();
    private readonly showDropdown = new ObservableValue<boolean>(false);
    private readonly text = new ObservableValue<string>("");
    private data: IReadyableReadonlyObservableArray<IWorkItemEntry>;

    constructor(props: ILinkedWorkItemsPullRequestDetailsProps, context: IVssComponentContext) {
        super(props);

        const viewService = getLinkedWorkItemsPullRequestDetailViewService(context.pageContext);
        this.data = viewService.getLinkedWorkItems(props.repository.project.id, props.repository.id, props.pullRequestId);

        this.readonly = !userHasClaim(context.pageContext, UserClaims.Member);

        if (!this.readonly) {
            this.headerCommandBarItems.push({
                ariaLabel: Resources.LinkWorkItem,
                hideDropdownIcon: true,
                iconProps: { iconName: "Add" },
                id: "add-work-item",
                onActivate: this.showWorkItemsDropdown,
                tooltipProps: { text: Resources.LinkWorkItem }
            });
        }
    }

    public render() {
        return this.renderDataComponent();
    }

    private renderDataComponent = () => {
        const viewService = getLinkedWorkItemsPullRequestDetailViewService(this.context.pageContext);

        return (
            <div className="flex-column rhythm-vertical-8">
                <Header
                    className="no-padding flex-grow"
                    commandBarItems={this.headerCommandBarItems}
                    ref={this.headerRef}
                    title={Resources.WorkItems}
                />
                <Observer errorMessage={this.errorMessage}>
                    {(props: { errorMessage: string }) =>
                        props.errorMessage ? (
                            <MessageCard onDismiss={() => (this.errorMessage.value = "")} severity={MessageCardSeverity.Error}>
                                {props.errorMessage}
                            </MessageCard>
                        ) : null
                    }
                </Observer>
                <Observer showDropdown={this.showDropdown}>
                    {(props: { showDropdown: boolean }) =>
                        props.showDropdown ? (
                            <ControlledLinkedWorkItemsDropdown
                                excludedSearchIds={viewService.getLinkedWorkItemIds()}
                                focusOnMount={true}
                                onBlur={() => (this.showDropdown.value = false)}
                                onTextChanged={this.onTextChanged}
                                onValueChanged={this.onValueChanged}
                                selection={this.selection}
                                text={this.text}
                                workItems={viewService.getAvailableWorkItems()}
                            />
                        ) : null
                    }
                </Observer>
                <Observer data={this.data} ready={this.data.ready}>
                    {props => {
                        // We need fakeObserver here only due to LinkedWorkItemsTable which expects IObservable as a provider.
                        // In fact we do not use subscribe or other specific stuff here.
                        const fakeObserver = new ObservableCollection<IListBoxItem<WorkItem>>();
                        fakeObserver.push(props.data);

                        return (
                            <LinkedWorkItemsTable
                                className="linked-wit-pr-details"
                                expanded={false}
                                itemProvider={fakeObserver}
                                onActivate={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IWorkItemEntry>) => {
                                    if (!event.defaultPrevented) {
                                        onClickFPS(this.context.pageContext, tableRow.data.href!, true, event);
                                    }
                                }}
                                onRemoveItem={this.onRemoveWorkItem}
                                readonly={this.readonly}
                                renderNoItemsContent={() =>
                                    props.ready ? (
                                        this.readonly ? (
                                            <div className="repos-pr-no-items-well">{Resources.NoWorkItems}</div>
                                        ) : (
                                            <div
                                                className="repos-pr-no-items-well cursor-pointer"
                                                onClick={this.showWorkItemsDropdown}
                                                onKeyPress={event => {
                                                    if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                                                        this.showWorkItemsDropdown();
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {Resources.NoWorkItems}
                                            </div>
                                        )
                                    ) : (
                                        <div className="repos-pr-no-items-well">{Resources.Loading}</div>
                                    )
                                }
                            />
                        );
                    }}
                </Observer>
            </div>
        );
    };

    private onRemoveWorkItem = (item: IListBoxItem<WorkItem>) => {
        const { repository, pullRequestId } = this.props;

        item.data &&
            getLinkedWorkItemsPullRequestDetailViewService(this.context.pageContext)
                .removeLinkedWorkItem(item.data, repository.project.id, repository.id, pullRequestId)
                .then(() => {
                    // On delete, set focus to Add button, to cover 2 scenarios:
                    // a) deleting the only item, and
                    // b) deleting the last item of a few.
                    this.headerRef.current?.focus({ commandBarItemId: "add-work-item" });
                })
                .catch(() => (this.errorMessage.value = format(Resources.FailedToRemoveWorkItem, item.data?.id)));
    };

    private onTextChanged = (ev: any, text: string) => {
        this.text.value = text;
        getLinkedWorkItemsPullRequestDetailViewService(this.context.pageContext).updateAvailableWorkItemsForFilter(text);
    };

    private onValueChanged = (newItem: IListBoxItem<WorkItem> | undefined) => {
        const { repository, pullRequestId } = this.props;

        this.selection.value = []; // Always keep this clear

        // was throwing a lot of errors on PR page due to newItem being undefined sometimes, so added check
        newItem &&
            newItem.data &&
            getLinkedWorkItemsPullRequestDetailViewService(this.context.pageContext)
                .addLinkedWorkItem(newItem.data, repository.project.id, repository.id, pullRequestId)
                .catch(() => (this.errorMessage.value = format(Resources.FailedToAddWorkItem, newItem.data?.id)));
    };

    private showWorkItemsDropdown = () => {
        this.showDropdown.value = true;
        getLinkedWorkItemsPullRequestDetailViewService(this.context.pageContext).hydrateAvailableWorkItems();
    };
}

Components.add("WIT.LinkedWorkItemsPullRequestDetails", LinkedWorkItemsPullRequestDetails);
